import * as React from "react";
import { IKeyValue, resourceNameKeyValues } from "motw_core/Entity/IKeyValue";
import { GetDefaultKeyValue } from "source/app/infrastructure/Data/KeyValues/defaultKeyValue";
import { FormTextArea } from "source/app/presentation/Shared/Form/TextArea/FormTextArea";
import { StyleClassAuto } from "source/app/presentation/Shared/Styles/Auto/StyleClassAuto";
import { AreYouSure } from "source/app/services/Shared/RenderSwal/RenderSwal";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";

type TPsycheRadarSpoke = {
    id: string;
    label: string;
    angleDeg: number;
    labelCenterX: number | undefined;
    labelCenterY: number | undefined;
    color: string;
    curve: number;
    notes: string;
};

type TPsycheRadarPoint = {
    x: number;
    y: number;
};

type TPsycheRadarStoredSnapshot = {
    spokes: TPsycheRadarSpoke[];
    updatedAt: string;
    hidePaths: boolean;
    hideCenter: boolean;
    resetAt?: string;
};

type TPsycheRadarStoredMap = {
    id: string;
    name: string;
    snapshot: TPsycheRadarStoredSnapshot;
};

type TPsycheRadarStoredLibrary = {
    maps: TPsycheRadarStoredMap[];
    updatedAt: string;
};

const initialSpokes: TPsycheRadarSpoke[] = [
    { id: "seed-1", label: "xyz", angleDeg: 210, labelCenterX: undefined, labelCenterY: undefined, color: "#f08a73", curve: -0.14, notes: "" },
    { id: "seed-2", label: "xyz", angleDeg: 250, labelCenterX: undefined, labelCenterY: undefined, color: "#ef6f6c", curve: 0.08, notes: "" },
    { id: "seed-3", label: "xyz", angleDeg: 292, labelCenterX: undefined, labelCenterY: undefined, color: "#f2a65a", curve: -0.1, notes: "" },
    { id: "seed-4", label: "xyz", angleDeg: 330, labelCenterX: undefined, labelCenterY: undefined, color: "#f15bb5", curve: 0.12, notes: "" },
    { id: "seed-5", label: "xyz", angleDeg: 15, labelCenterX: undefined, labelCenterY: undefined, color: "#ff6f91", curve: -0.1, notes: "" },
    { id: "seed-6", label: "xyz", angleDeg: 60, labelCenterX: undefined, labelCenterY: undefined, color: "#f8961e", curve: 0.09, notes: "" },
    { id: "seed-7", label: "xyz", angleDeg: 115, labelCenterX: undefined, labelCenterY: undefined, color: "#f3722c", curve: -0.12, notes: "" },
    { id: "seed-8", label: "xyz", angleDeg: 165, labelCenterX: undefined, labelCenterY: undefined, color: "#f94144", curve: 0.1, notes: "" },
];

const psycheRadarStorageKey = "psyche_radar_spokes_v1";
const psycheRadarSyncedKey = "PsycheRadar_spokes";
const psycheRadarExportFilename = "maps.json";
const psycheRadarActiveMapIdStorageKey = "psyche_radar_active_map_id_v1";
const psycheRadarSyncIntervalMs = 10000;
const psycheRadarSyncDebounceMs = 600;
const emptyRadarSpokes: TPsycheRadarSpoke[] = [];

export function usePsycheRadarController() {
    const size = 640;
    const centerX = size / 2;
    const centerY = 332;
    const outerRadius = 250;
    const viewBoxTop = 40;
    const viewBoxHeight = 570;
    const innerRadius = 74;
    const centerDiscRadius = 48;
    const attackLength = 14;
    const handleHitRadius = 18;
    const nubHitRadius = 14;
    const activeNubHitRadius = 22;

    const [library, setLibrary] = React.useState<TPsycheRadarStoredLibrary>(
        () => LoadPersistedLibrary()
    );
    const hasPersistedLibraryRef = React.useRef(HasPersistedLibrary());
    const [selectedMapId, setSelectedMapId] = React.useState<string>(
        () => LoadActiveMapId()
    );
    const currentMap = GetSelectedMap(library, selectedMapId);
    const snapshot = currentMap.snapshot;
    const spokes = snapshot.spokes;
    const [selectedIndex, setSelectedIndex] = React.useState<number | null>(
        null
    );
    const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(
        null
    );
    const [isCenterHovered, setIsCenterHovered] = React.useState(false);
    const [isCenterHoverGlowVisible, setIsCenterHoverGlowVisible] =
        React.useState(false);
    const [isCenterHoverGlowArmed, setIsCenterHoverGlowArmed] =
        React.useState(false);
    const [isTouchZoomLocked, setIsTouchZoomLocked] = React.useState(false);
    const importFileInputRef = React.useRef<HTMLInputElement | null>(null);
    const previousSelectedIndexRef = React.useRef<number | null>(null);
    const [draggingIndex, setDraggingIndex] = React.useState<number | null>(
        null
    );
    const [labelDraggingIndex, setLabelDraggingIndex] = React.useState<
        number | null
    >(null);
    const [noteTooltipIndex, setNoteTooltipIndex] = React.useState<
        number | null
    >(null);
    const [draftLabel, setDraftLabel] = React.useState<string>("");
    const [draftNotes, setDraftNotes] = React.useState<string>("");
    const [draftMapName, setDraftMapName] = React.useState<string>("");
    const [isPathHiddenByCheckbox, setIsPathHiddenByCheckbox] =
        React.useState<boolean>(() => snapshot.hidePaths);
    const [isCenterHiddenByCheckbox, setIsCenterHiddenByCheckbox] =
        React.useState<boolean>(() => snapshot.hideCenter);
    const [pressedActionButton, setPressedActionButton] = React.useState<
        "add" | "rename" | "delete" | "reset" | null
    >(null);
    const [recentlyAddedSpokeId, setRecentlyAddedSpokeId] = React.useState<
        string | null
    >(null);
    const [visibleNewSpokeIds, setVisibleNewSpokeIds] = React.useState<Set<string>>(
        () => new Set()
    );
    const svgRef = React.useRef<SVGSVGElement | null>(null);
    const hasLoadedRemoteSnapshotRef = React.useRef(false);
    const saveTimeoutRef = React.useRef<number | undefined>(undefined);
    const spokeDragAnimationFrameRef = React.useRef<number | null>(null);
    const pendingSpokeAngleRef = React.useRef<number | null>(null);
    const didDragSpokeRef = React.useRef(false);
    const labelDragAnimationFrameRef = React.useRef<number | null>(null);
    const pendingLabelPointRef = React.useRef<TPsycheRadarPoint | null>(null);
    const didDragLabelRef = React.useRef(false);
    const labelDragLastPointRef = React.useRef<TPsycheRadarPoint | null>(null);
    const skipNextDraftSyncRef = React.useRef(false);
    const latestLibraryRef = React.useRef<TPsycheRadarStoredLibrary>(library);
    const activeTouchPointersRef = React.useRef<Set<number>>(new Set());
    const hasSelectedSpoke = selectedIndex !== null;
    const arePathsHidden = snapshot.hidePaths;
    const spokeRenderData = spokes.map((spoke, index) => {
        const isSelected = selectedIndex === index;
        const isInteractable = (!hasSelectedSpoke || isSelected) && !isTouchZoomLocked;
        const isHovered = isInteractable && hoveredIndex === index;
        const isDragging = draggingIndex === index;
        const isActive = isSelected || isHovered || isDragging;
        const spokeOpacity = isSelected || !hasSelectedSpoke ? 1 : 0.24;
        const hoverAccent = "#d8ffec";
        const selectedAccent = "#f4ead7";
        const handlePoint = PolarToCartesian(
            centerX,
            centerY,
            centerDiscRadius,
            spoke.angleDeg
        );
        const startPoint = PolarToCartesian(
            centerX,
            centerY,
            outerRadius + attackLength,
            spoke.angleDeg
        );
        const controlPoint = PolarToCartesian(
            centerX,
            centerY,
            outerRadius * 0.58,
            spoke.angleDeg + spoke.curve * 160
        );
        const defaultLabelCenter = GetDefaultLabelCenter(
            centerX,
            spoke.angleDeg
        );
        const labelCenterX = spoke.labelCenterX ?? defaultLabelCenter.x;
        const labelCenterY = spoke.labelCenterY ?? defaultLabelCenter.y;
        const labelDistanceFromCenter = GetDistanceBetweenPoints(
            centerX,
            centerY,
            labelCenterX,
            labelCenterY
        );
        const labelLayout = GetLabelLayout(
            spoke.label,
            labelCenterX,
            labelCenterY,
            labelDistanceFromCenter,
            outerRadius
        );
        const noteTooltipLayout = GetNoteTooltipLayout(
            spoke.notes,
            labelLayout,
            size
        );
        const normalizedLabelDistance = Clamp(
            labelDistanceFromCenter / (outerRadius + 70),
            0,
            1
        );
        const spokeVisibility = Clamp(
            1 - Math.pow(normalizedLabelDistance, 1.55) * 0.92,
            0.04,
            1
        );
        const isLabelBeyondOuterRadius = labelDistanceFromCenter > outerRadius;
        const labelConnector = GetLabelConnectorPoint(handlePoint, labelLayout);
        const pathD = `M ${handlePoint.x} ${handlePoint.y} Q ${controlPoint.x} ${controlPoint.y} ${labelConnector.x} ${labelConnector.y}`;
        const dashSegments = GetTaperedDashSegments(
            handlePoint,
            controlPoint,
            labelConnector
        );
        const spokeStroke = isSelected && !isDragging
            ? selectedAccent
            : isActive
                ? hoverAccent
                : "url(#attack-stroke)";
        const labelColor = isSelected && !isDragging
            ? selectedAccent
            : isActive
                ? hoverAccent
                : "#f4ead7";
        const trimmedLabel = spoke.label.trimStart();
        const hasAsteriskPrefix = trimmedLabel.startsWith("*");
        const hasPlusPrefix = trimmedLabel.startsWith("+");
        const hasMinusPrefix = trimmedLabel.startsWith("-");
        const isEntering = spoke.id === recentlyAddedSpokeId;
        const isVisibleNewSpoke = visibleNewSpokeIds.has(spoke.id);
        const isLabelDragging = isInteractable && labelDraggingIndex === index;
        const impactFlare = GetImpactFlareStyle(
            spoke,
            index,
            isActive,
            spokeVisibility
        );

        return {
            spoke,
            index,
            isSelected,
            isActive,
            handlePoint,
            labelLayout,
            spokeVisibility,
            pathD,
            dashSegments,
            spokeStroke,
            labelColor,
            hasAsteriskPrefix,
            hasPlusPrefix,
            hasMinusPrefix,
            isLabelBeyondOuterRadius,
            isEntering,
            isVisibleNewSpoke,
            isLabelDragging,
            isInteractable,
            spokeOpacity,
            arePathsHidden,
            isCenterHiddenByCheckbox,
            impactFlare,
            noteTooltipLayout,
        };
    });
    const isCenterInDanger = spokeRenderData.some(
        ({ isLabelDragging, isLabelBeyondOuterRadius }) =>
            isLabelDragging && isLabelBeyondOuterRadius
    );
    const centerDiscFill = isCenterInDanger ? "#4f0c12" : "#120d0a";

    React.useEffect(() => {
        latestLibraryRef.current = library;
    }, [library]);

    React.useEffect(() => {
        SaveActiveMapId(selectedMapId);
    }, [selectedMapId]);

    React.useEffect(() => {
        if (library.maps.length === 0) {
            return;
        }

        if (library.maps.some((map) => map.id === selectedMapId)) {
            return;
        }

        setSelectedMapId(library.maps[0].id);
    }, [library, selectedMapId]);

    React.useEffect(() => {
        setIsPathHiddenByCheckbox(snapshot.hidePaths);
    }, [snapshot.hidePaths]);

    React.useEffect(() => {
        setIsCenterHiddenByCheckbox(snapshot.hideCenter);
    }, [snapshot.hideCenter]);

    React.useEffect(() => {
        if (isCenterHovered) {
            setIsCenterHoverGlowVisible(true);
            const frameId = window.requestAnimationFrame(() => {
                setIsCenterHoverGlowArmed(true);
            });

            return () => window.cancelAnimationFrame(frameId);
        }

        setIsCenterHoverGlowArmed(false);

        const timeoutId = window.setTimeout(() => {
            setIsCenterHoverGlowVisible(false);
        }, 1100);

        return () => window.clearTimeout(timeoutId);
    }, [isCenterHovered]);

    React.useEffect(() => {
        if (!isTouchZoomLocked) {
            return;
        }

        setSelectedIndex(null);
        setHoveredIndex(null);
        setNoteTooltipIndex(null);
        setDraggingIndex(null);
        setLabelDraggingIndex(null);
    }, [isTouchZoomLocked]);

    React.useEffect(() => {
        const handleTouchStart = (event: TouchEvent) => {
            if (event.touches.length < 2) {
                return;
            }

            const nextPointers = new Set(activeTouchPointersRef.current);
            for (let index = 0; index < event.touches.length; index += 1) {
                nextPointers.add(event.touches[index].identifier);
            }

            activeTouchPointersRef.current = nextPointers;
            setIsTouchZoomLocked(true);
        };

        const handleTouchEnd = (event: TouchEvent) => {
            const nextPointers = new Set(activeTouchPointersRef.current);

            for (let index = 0; index < event.changedTouches.length; index += 1) {
                nextPointers.delete(event.changedTouches[index].identifier);
            }

            activeTouchPointersRef.current = nextPointers;

            if (event.touches.length < 2) {
                setIsTouchZoomLocked(false);
            }
        };

        const handleTouchCancel = handleTouchEnd;

        window.addEventListener("touchstart", handleTouchStart, {
            passive: true,
        });
        window.addEventListener("touchend", handleTouchEnd, {
            passive: true,
        });
        window.addEventListener("touchcancel", handleTouchCancel, {
            passive: true,
        });

        return () => {
            window.removeEventListener("touchstart", handleTouchStart);
            window.removeEventListener("touchend", handleTouchEnd);
            window.removeEventListener("touchcancel", handleTouchCancel);
        };
    }, []);

    React.useEffect(() => {
        if (!recentlyAddedSpokeId) {
            return;
        }

        const revealTimeoutId = window.setTimeout(() => {
            setVisibleNewSpokeIds((current) => {
                if (current.has(recentlyAddedSpokeId)) {
                    return current;
                }

                const next = new Set(current);
                next.add(recentlyAddedSpokeId);
                return next;
            });
        }, 60);

        const cleanupTimeoutId = window.setTimeout(() => {
            setRecentlyAddedSpokeId(null);
        }, 700);

        return () => {
            window.clearTimeout(revealTimeoutId);
            window.clearTimeout(cleanupTimeoutId);
        };
    }, [recentlyAddedSpokeId]);

    React.useEffect(() => {
        if (draggingIndex === null) {
            return;
        }

        const flushPendingSpokeAngle = () => {
            if (spokeDragAnimationFrameRef.current !== null) {
                window.cancelAnimationFrame(spokeDragAnimationFrameRef.current);
                spokeDragAnimationFrameRef.current = null;
            }

            const nextAngle = pendingSpokeAngleRef.current;
            pendingSpokeAngleRef.current = null;

            if (nextAngle === null) {
                return;
            }

            UpdateSpokes((current) =>
                current.map((spoke, index) =>
                    index === draggingIndex
                        ? {
                              ...spoke,
                              angleDeg: nextAngle,
                          }
                        : spoke
                )
            );
        };

        const scheduleDragFrame = () => {
            if (spokeDragAnimationFrameRef.current !== null) {
                return;
            }

            spokeDragAnimationFrameRef.current = window.requestAnimationFrame(() => {
                spokeDragAnimationFrameRef.current = null;
                flushPendingSpokeAngle();
            });
        };

        const handlePointerMove = (event: PointerEvent) => {
            const svg = svgRef.current;

            if (!svg) {
                return;
            }

            didDragSpokeRef.current = true;
            const point = GetSvgPoint(svg, event.clientX, event.clientY, size);
            const nextAngle = GetAngleFromPoint(
                centerX,
                centerY,
                point.x,
                point.y
            );
            pendingSpokeAngleRef.current = nextAngle;
            scheduleDragFrame();
        };

        const handlePointerUp = () => {
            flushPendingSpokeAngle();
            setDraggingIndex(null);
            if (didDragSpokeRef.current) {
                setSelectedIndex(null);
            }
            didDragSpokeRef.current = false;
        };

        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);
        window.addEventListener("pointercancel", handlePointerUp);

        return () => {
            if (spokeDragAnimationFrameRef.current !== null) {
                window.cancelAnimationFrame(spokeDragAnimationFrameRef.current);
                spokeDragAnimationFrameRef.current = null;
            }

            pendingSpokeAngleRef.current = null;
            didDragSpokeRef.current = false;
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerUp);
            window.removeEventListener("pointercancel", handlePointerUp);
        };
    }, [centerX, centerY, draggingIndex, size]);

    React.useEffect(() => {
        if (labelDraggingIndex === null) {
            return;
        }

        const flushPendingLabelPoint = () => {
            if (labelDragAnimationFrameRef.current !== null) {
                window.cancelAnimationFrame(labelDragAnimationFrameRef.current);
                labelDragAnimationFrameRef.current = null;
            }

            const nextPoint = pendingLabelPointRef.current;
            pendingLabelPointRef.current = null;

            if (!nextPoint) {
                return;
            }

            UpdateSpokes((current) =>
                current.map((spoke, index) =>
                    index === labelDraggingIndex
                        ? {
                              ...spoke,
                              labelCenterX: nextPoint.x,
                              labelCenterY: nextPoint.y,
                          }
                        : spoke
                )
            );
        };

        const scheduleLabelDragFrame = () => {
            if (labelDragAnimationFrameRef.current !== null) {
                return;
            }

            labelDragAnimationFrameRef.current = window.requestAnimationFrame(
                () => {
                    labelDragAnimationFrameRef.current = null;
                    flushPendingLabelPoint();
                }
            );
        };

        const handlePointerMove = (event: PointerEvent) => {
            const svg = svgRef.current;

            if (!svg) {
                return;
            }

            didDragLabelRef.current = true;
            const nextPoint = GetSvgPoint(
                svg,
                event.clientX,
                event.clientY,
                size
            );
            pendingLabelPointRef.current = nextPoint;
            labelDragLastPointRef.current = nextPoint;
            scheduleLabelDragFrame();
        };

        const handlePointerUp = () => {
            const shouldDeleteLabel =
                didDragLabelRef.current &&
                labelDragLastPointRef.current !== null &&
                GetDistanceBetweenPoints(
                    centerX,
                    centerY,
                    labelDragLastPointRef.current.x,
                    labelDragLastPointRef.current.y
                ) > outerRadius;

            flushPendingLabelPoint();
            if (didDragLabelRef.current) {
                if (shouldDeleteLabel) {
                    UpdateSpokes((current) =>
                        current.filter(
                            (_, index) => index !== labelDraggingIndex
                        )
                    );
                }
                setSelectedIndex(null);
                setHoveredIndex(null);
            }
            setLabelDraggingIndex(null);
            didDragLabelRef.current = false;
            labelDragLastPointRef.current = null;
        };

        const handlePointerCancel = () => {
            flushPendingLabelPoint();
            setLabelDraggingIndex(null);
            didDragLabelRef.current = false;
            labelDragLastPointRef.current = null;
        };

        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);
        window.addEventListener("pointercancel", handlePointerCancel);

        return () => {
            if (labelDragAnimationFrameRef.current !== null) {
                window.cancelAnimationFrame(labelDragAnimationFrameRef.current);
                labelDragAnimationFrameRef.current = null;
            }

            pendingLabelPointRef.current = null;
            didDragLabelRef.current = false;
            labelDragLastPointRef.current = null;
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerUp);
            window.removeEventListener("pointercancel", handlePointerCancel);
        };
    }, [centerX, centerY, labelDraggingIndex, outerRadius, size]);

    React.useEffect(() => {
        if (selectedIndex === null) {
            if (previousSelectedIndexRef.current !== null) {
                setDraftLabel("");
                setDraftNotes("");
            }

            previousSelectedIndexRef.current = selectedIndex;
            return;
        }

        if (skipNextDraftSyncRef.current) {
            skipNextDraftSyncRef.current = false;
            previousSelectedIndexRef.current = selectedIndex;
            return;
        }

        setDraftLabel(spokes[selectedIndex]?.label ?? "");
        setDraftNotes(spokes[selectedIndex]?.notes ?? "");
        previousSelectedIndexRef.current = selectedIndex;
    }, [selectedIndex, spokes]);

    React.useEffect(() => {
        setDraftMapName(currentMap.name ?? "");
    }, [currentMap.id, currentMap.name]);

    React.useEffect(() => {
        PersistLibraryToLocalStorage(library);

        if (!hasLoadedRemoteSnapshotRef.current) {
            return;
        }

        if (saveTimeoutRef.current !== undefined) {
            window.clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = window.setTimeout(() => {
            SaveLibraryToServerAsync(library);
        }, psycheRadarSyncDebounceMs);

        return () => {
            if (saveTimeoutRef.current !== undefined) {
                window.clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [library]);

    React.useEffect(() => {
        const handleBeforeUnload = () => {
            PersistLibraryToLocalStorage(latestLibraryRef.current);

            if (saveTimeoutRef.current !== undefined) {
                window.clearTimeout(saveTimeoutRef.current);
            }

            void SaveLibraryToServerAsync(latestLibraryRef.current);
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    React.useEffect(() => {
        let isCancelled = false;

        const SyncFromServerAsync = async () => {
            const remoteLibrary = await LoadLibraryFromServerAsync();

            if (isCancelled) {
                return;
            }

            if (!hasPersistedLibraryRef.current) {
                setLibrary(remoteLibrary);
                latestLibraryRef.current = remoteLibrary;
                PersistLibraryToLocalStorage(remoteLibrary);
                hasPersistedLibraryRef.current = true;
                hasLoadedRemoteSnapshotRef.current = true;
                return;
            }

            const snapshotComparison = CompareLibraries(library, remoteLibrary);

            if (snapshotComparison < 0) {
                setLibrary(remoteLibrary);
                latestLibraryRef.current = remoteLibrary;
                PersistLibraryToLocalStorage(remoteLibrary);
            } else if (snapshotComparison > 0) {
                await SaveLibraryToServerAsync(library);
            }

            hasLoadedRemoteSnapshotRef.current = true;
        };

        SyncFromServerAsync();

        return () => {
            isCancelled = true;
        };
    }, []);

    React.useEffect(() => {
        const intervalId = window.setInterval(async () => {
            if (draggingIndex !== null) {
                return;
            }

            const remoteLibrary = await LoadLibraryFromServerAsync();

            setLibrary((currentLibrary) => {
                if (CompareLibraries(currentLibrary, remoteLibrary) < 0) {
                    latestLibraryRef.current = remoteLibrary;
                    PersistLibraryToLocalStorage(remoteLibrary);
                    return remoteLibrary;
                }

                return currentLibrary;
            });
        }, psycheRadarSyncIntervalMs);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [draggingIndex]);

    function UpdateSpokes(
        update:
            | TPsycheRadarSpoke[]
            | ((current: TPsycheRadarSpoke[]) => TPsycheRadarSpoke[]),
        options?: {
            resetAll?: boolean;
        }
    ) {
        UpdateCurrentMap((currentMap) => {
            const currentSnapshot = currentMap.snapshot;
            const nextSpokes =
                typeof update === "function"
                    ? update(currentSnapshot.spokes)
                    : update;
            const now = new Date().toISOString();

            return {
                ...currentMap,
                snapshot: {
                    spokes: nextSpokes,
                    updatedAt: now,
                    hidePaths: currentSnapshot.hidePaths,
                    hideCenter: currentSnapshot.hideCenter,
                    ...(options?.resetAll ? { resetAt: now } : {}),
                },
            };
        });
    }

    function UpdateCurrentMap(
        update: (
            currentMap: TPsycheRadarStoredMap
        ) => TPsycheRadarStoredMap
    ) {
        setLibrary((currentLibrary) => {
            const nextLibrary = UpdateLibraryMap(
                currentLibrary,
                selectedMapId,
                update
            );

            latestLibraryRef.current = nextLibrary;
            PersistLibraryToLocalStorage(nextLibrary);

            return nextLibrary;
        });
    }

    const handleAddSpoke = () => {
        const nextLabel = draftLabel.trim();

        if (!nextLabel) {
            return;
        }

        const nextAngle = GetNextAvailableAngle(spokes);
        const nextColor = GetNextSpokeColor(spokes.length);
        const nextSpoke: TPsycheRadarSpoke = {
            id: CreateSpokeId(),
            label: nextLabel,
            angleDeg: nextAngle,
            labelCenterX: GetDefaultLabelCenter(centerX, nextAngle).x,
            labelCenterY: GetDefaultLabelCenter(centerX, nextAngle).y,
            color: nextColor,
            curve: 0.08,
            notes: "",
        };

        UpdateSpokes((current) => [...current, nextSpoke]);
        setSelectedIndex(spokes.length);
        setHoveredIndex(null);
        setRecentlyAddedSpokeId(nextSpoke.id);
        setDraftLabel("");
        setDraftNotes("");
        skipNextDraftSyncRef.current = true;
    };

    const handleDeleteSelectedSpoke = () => {
        if (selectedIndex === null) {
            return;
        }

        UpdateSpokes((current) =>
            current.filter((_, index) => index !== selectedIndex)
        );
        setSelectedIndex(null);
        setHoveredIndex(null);
        setNoteTooltipIndex(null);
        setDraggingIndex(null);
        setLabelDraggingIndex(null);
        setDraftLabel("");
        setDraftNotes("");
    };

    const handleRenameSelectedSpoke = () => {
        if (selectedIndex === null) {
            return;
        }

        skipNextDraftSyncRef.current = true;
        UpdateSpokes((current) =>
            current.map((spoke, index) =>
                index === selectedIndex
                    ? {
                          ...spoke,
                          label: draftLabel.trim() || "xyz",
                      }
                    : spoke
            )
        );
        setDraftLabel("");
    };

    const handleSaveSelectedNotes = () => {
        if (selectedIndex === null) {
            return;
        }

        UpdateSpokes((current) =>
            current.map((spoke, index) =>
                index === selectedIndex
                    ? {
                          ...spoke,
                          notes: draftNotes,
                      }
                    : spoke
            )
        );
    };

    const handleClearSelectedNotes = () => {
        if (selectedIndex === null) {
            return;
        }

        setDraftNotes("");
        UpdateSpokes((current) =>
            current.map((spoke, index) =>
                index === selectedIndex
                    ? {
                          ...spoke,
                          notes: "",
                      }
                    : spoke
            )
        );
    };

    const handleResetSpokes = () => {
        AreYouSure("Are you sure? This will clear all spokes", () => {
            UpdateSpokes([], { resetAll: true });
            setSelectedIndex(null);
            setHoveredIndex(null);
            setNoteTooltipIndex(null);
            setDraggingIndex(null);
            setLabelDraggingIndex(null);
            setDraftLabel("");
            setDraftNotes("");
        });
    };

    const handleHidePathsChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const nextHidePaths = event.target.checked;

        setIsPathHiddenByCheckbox(nextHidePaths);
        UpdateCurrentMap((currentMap) => ({
            ...currentMap,
            snapshot: {
                ...currentMap.snapshot,
                hidePaths: nextHidePaths,
                updatedAt: new Date().toISOString(),
            },
        }));
    };

    const handleHideCenterChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const nextHideCenter = event.target.checked;

        setIsCenterHiddenByCheckbox(nextHideCenter);
        UpdateCurrentMap((currentMap) => ({
            ...currentMap,
            snapshot: {
                ...currentMap.snapshot,
                hideCenter: nextHideCenter,
                updatedAt: new Date().toISOString(),
            },
        }));
    };

    const handleExportRadarData = () => {
        const libraryToExport = latestLibraryRef.current;
        const exportFilename = GetPsycheRadarExportFilename(currentMap.name);
        const blob = new Blob(
            [
                JSON.stringify(
                    {
                        ...libraryToExport,
                        activeMapId:
                            selectedMapId || libraryToExport.maps[0]?.id || "",
                    },
                    null,
                    2
                ),
            ],
            {
                type: "application/json;charset=utf-8",
            }
        );
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = exportFilename;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
    };

    const handleImportRadarDataClick = () => {
        importFileInputRef.current?.click();
    };

    const handleImportRadarDataChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        event.target.value = "";

        if (!file) {
            return;
        }

        try {
            const text = await file.text();
            const parsedLibrary = ParseStoredLibrary(JSON.parse(text));
            const importedMapNameBase = GetPsycheRadarMapNameFromFilename(
                file.name
            );

            if (!parsedLibrary) {
                return;
            }

            const importedMaps = parsedLibrary.library.maps.map((map, index) =>
                CreateMapFromSnapshot(
                    map.snapshot,
                    ShouldUseImportedMapFilenameName(map.name)
                        ? BuildImportedMapName(importedMapNameBase, index)
                        : map.name
                )
            );

            if (importedMaps.length === 0) {
                return;
            }

            const importedMapNameKeys = importedMaps.map((map) =>
                GetNormalizedMapNameKey(map.name)
            );
            const hasNameConflict = importedMapNameKeys.some((importedNameKey) =>
                library.maps.some(
                    (existingMap) =>
                        GetNormalizedMapNameKey(existingMap.name) === importedNameKey
                )
            );

            const ApplyImportedMaps = () => {
                const nextMaps = [...library.maps];

                for (const importedMap of importedMaps) {
                    const importedMapNameKey = GetNormalizedMapNameKey(
                        importedMap.name
                    );
                    const existingMapIndex = nextMaps.findIndex(
                        (existingMap) =>
                            GetNormalizedMapNameKey(existingMap.name) ===
                            importedMapNameKey
                    );

                    if (existingMapIndex >= 0) {
                        nextMaps[existingMapIndex] = {
                            ...importedMap,
                            id: nextMaps[existingMapIndex].id,
                            name: nextMaps[existingMapIndex].name,
                        };
                        continue;
                    }

                    nextMaps.push(importedMap);
                }

                const nextLibrary = NormalizeLibrary({
                    maps: nextMaps,
                    updatedAt: new Date().toISOString(),
                });
                const nextSelectedMapId =
                    nextLibrary.maps.find(
                        (map) =>
                            GetNormalizedMapNameKey(map.name) ===
                            importedMapNameKeys[0]
                    )?.id ?? nextLibrary.maps[0]?.id ?? "";

                latestLibraryRef.current = nextLibrary;
                setLibrary(nextLibrary);
                PersistLibraryToLocalStorage(nextLibrary);
                setSelectedMapId(nextSelectedMapId);
                setSelectedIndex(null);
                setHoveredIndex(null);
                setNoteTooltipIndex(null);
                setDraggingIndex(null);
                setLabelDraggingIndex(null);
                setDraftLabel("");
                setDraftNotes("");
                setDraftMapName(
                    nextLibrary.maps.find((map) => map.id === nextSelectedMapId)
                        ?.name ?? ""
                );
            };

            if (hasNameConflict) {
                AreYouSure(
                    `A map with that name already exists. Overwrite it? Cancel keeps your current maps.`,
                    () => {
                        ApplyImportedMaps();
                    }
                );
                return;
            }

            ApplyImportedMaps();
        } catch {
            // Ignore malformed imports.
        }
    };

    const handleCreateMap = () => {
        const nextMap = CreateBlankMap(
            GetNextMapName(library.maps),
            GetNextMapId()
        );
        const nextLibrary = {
            ...library,
            maps: [...library.maps, nextMap],
            updatedAt: new Date().toISOString(),
        };

        latestLibraryRef.current = nextLibrary;
        setLibrary(nextLibrary);
        PersistLibraryToLocalStorage(nextLibrary);
        setSelectedMapId(nextMap.id);
        setSelectedIndex(null);
        setHoveredIndex(null);
        setNoteTooltipIndex(null);
        setDraggingIndex(null);
        setLabelDraggingIndex(null);
        setDraftLabel("");
        setDraftNotes("");
        setDraftMapName(nextMap.name);
    };

    const handleSelectedMapChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const nextMapId = event.target.value;
        const nextMap = library.maps.find((map) => map.id === nextMapId);

        setSelectedMapId(nextMapId);
        setSelectedIndex(null);
        setHoveredIndex(null);
        setNoteTooltipIndex(null);
        setDraggingIndex(null);
        setLabelDraggingIndex(null);
        setDraftLabel("");
        setDraftNotes("");
        setDraftMapName(nextMap?.name ?? "");
    };

    const handleRenameSelectedMap = () => {
        const nextMapName = draftMapName.trim();

        if (!nextMapName) {
            return;
        }

        const nextLibrary = UpdateLibraryMap(
            library,
            selectedMapId,
            (currentMap) => ({
                ...currentMap,
                name: nextMapName,
            })
        );

        latestLibraryRef.current = nextLibrary;
        setLibrary(nextLibrary);
        PersistLibraryToLocalStorage(nextLibrary);
        setDraftMapName(nextMapName);
    };

    const handleDeleteSelectedMap = () => {
        if (library.maps.length === 0) {
            return;
        }

        AreYouSure(
            `Delete "${currentMap.name || "this map"}"? This will remove its data.`,
            () => {
                const currentIndex = library.maps.findIndex(
                    (map) => map.id === selectedMapId
                );
                const nextMaps = library.maps.filter(
                    (map) => map.id !== selectedMapId
                );
                const fallbackMap =
                    nextMaps[currentIndex] ??
                    nextMaps[currentIndex - 1] ??
                    nextMaps[0] ??
                    CreateBlankMap("Map 1");
                const nextLibrary = NormalizeLibrary({
                    maps: nextMaps.length > 0 ? nextMaps : [fallbackMap],
                    updatedAt: new Date().toISOString(),
                });
                const nextSelectedMapId =
                    nextLibrary.maps.find((map) => map.id === fallbackMap.id)?.id ??
                    nextLibrary.maps[0]?.id ??
                    "";

                latestLibraryRef.current = nextLibrary;
                setLibrary(nextLibrary);
                PersistLibraryToLocalStorage(nextLibrary);
                setSelectedMapId(nextSelectedMapId);
                setSelectedIndex(null);
                setHoveredIndex(null);
                setNoteTooltipIndex(null);
                setDraggingIndex(null);
                setLabelDraggingIndex(null);
                setDraftLabel("");
                setDraftNotes("");
                setDraftMapName(
                    nextLibrary.maps.find((map) => map.id === nextSelectedMapId)
                        ?.name ?? ""
                );
            }
        );
    };

    const handleRadarPointerDown = (
        event: React.PointerEvent<SVGElement>
    ) => {
        if (isTouchZoomLocked) {
            return;
        }

        const target = event.target;

        if (
            target instanceof Element &&
            target.closest('[data-radar-interactive="true"]')
        ) {
            return;
        }

        setSelectedIndex(null);
        setNoteTooltipIndex(null);
        setHoveredIndex(null);
    };


    return {
        size,
        centerX,
        centerY,
        outerRadius,
        viewBoxTop,
        viewBoxHeight,
        innerRadius,
        centerDiscRadius,
        attackLength,
        handleHitRadius,
        nubHitRadius,
        activeNubHitRadius,
        spokes,
        spokeRenderData,
        isCenterInDanger,
        centerDiscFill,
        svgRef,
        selectedIndex,
        setSelectedIndex,
        hoveredIndex,
        setHoveredIndex,
        isCenterHovered,
        setIsCenterHovered,
        isCenterHoverGlowVisible,
        isCenterHoverGlowArmed,
        setIsCenterHoverGlowVisible,
        setIsCenterHoverGlowArmed,
        draggingIndex,
        setDraggingIndex,
        labelDraggingIndex,
        setLabelDraggingIndex,
        noteTooltipIndex,
        setNoteTooltipIndex,
        draftLabel,
        setDraftLabel,
        draftNotes,
        setDraftNotes,
        isPathHiddenByCheckbox,
        setIsPathHiddenByCheckbox,
        isCenterHiddenByCheckbox,
        setIsCenterHiddenByCheckbox,
        pressedActionButton,
        setPressedActionButton,
        recentlyAddedSpokeId,
        setRecentlyAddedSpokeId,
        visibleNewSpokeIds,
        setVisibleNewSpokeIds,
        handleRadarPointerDown,
        handleAddSpoke,
        handleRenameSelectedSpoke,
        handleDeleteSelectedSpoke,
        handleResetSpokes,
        handleClearSelectedNotes,
        handleSaveSelectedNotes,
        handleHidePathsChange,
        handleHideCenterChange,
        handleExportRadarData,
        handleImportRadarDataClick,
        handleImportRadarDataChange,
        handleCreateMap,
        handleSelectedMapChange,
        handleRenameSelectedMap,
        handleDeleteSelectedMap,
        maps: library.maps,
        selectedMapId,
        draftMapName,
        setDraftMapName,
        isTouchZoomLocked,
        importFileInputRef,
    };
}

function PolarToCartesian(
    cx: number,
    cy: number,
    radius: number,
    angleDeg: number
) {
    const angleInRadians = ((angleDeg - 90) * Math.PI) / 180;

    return {
        x: cx + radius * Math.cos(angleInRadians),
        y: cy + radius * Math.sin(angleInRadians),
    };
}

function GetAngleFromPoint(
    cx: number,
    cy: number,
    x: number,
    y: number
) {
    const radians = Math.atan2(y - cy, x - cx);
    const degrees = (radians * 180) / Math.PI + 90;

    return NormalizeAngle(degrees);
}

function GetSvgPoint(
    svg: SVGSVGElement,
    clientX: number,
    clientY: number,
    size: number
): TPsycheRadarPoint {
    const rect = svg.getBoundingClientRect();
    const scaleX = size / rect.width;
    const scaleY = size / rect.height;

    return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
    };
}

function GetLabelLayout(
    label: string,
    x: number,
    y: number,
    distanceFromCenter: number,
    outerRadius: number
) {
    const lines = WrapLabelText(label, 10);
    const longestLineLength = lines.reduce(
        (longest, line) => Math.max(longest, line.length),
        1
    );
    const normalizedDistance = Clamp(
        distanceFromCenter / (outerRadius + 70),
        0,
        1
    );
    const distanceFontScale = Lerp(1, 0.05, normalizedDistance);
    const fontSize = Math.max(
        8,
        Math.min(
            20,
            Math.round(
                (22 -
                    Math.max(0, longestLineLength - 6) * 1.1 -
                    Math.max(0, lines.length - 1) * 1.5) *
                    distanceFontScale
            )
        )
    );
    const lineHeight = Math.max(14, Math.round(fontSize * 1.08));
    const width = Math.max(
        36,
        Math.min(160, Math.round(longestLineLength * fontSize * 0.68 + 12))
    );
    const verticalPadding = 8;
    const height = lines.length * lineHeight + verticalPadding;
    const lineCenterYs = lines.map(
        (_line, index) =>
            y - ((lines.length - 1) * lineHeight) / 2 +
            index * lineHeight
    );

    return {
        x: x - width / 2,
        y: y - height / 2,
        width,
        height,
        textX: x,
        lineCenterYs,
        fontSize,
        lineHeight,
        lines,
    };
}

function GetNoteTooltipLayout(
    notes: string,
    labelLayout: ReturnType<typeof GetLabelLayout>,
    size: number
) {
    const trimmedNotes = notes.trim();

    if (!trimmedNotes) {
        return undefined;
    }

    const wrappedLines = WrapLabelText(trimmedNotes, 24);
    const lines =
        wrappedLines.length > 5
            ? [...wrappedLines.slice(0, 4), `${wrappedLines[4].slice(0, 21)}...`]
            : wrappedLines;
    const longestLineLength = lines.reduce(
        (longest, line) => Math.max(longest, line.length),
        1
    );
    const lineHeight = 14;
    const width = Math.max(
        72,
        Math.min(220, Math.round(longestLineLength * 7 + 18))
    );
    const height = lines.length * lineHeight + 12;
    const labelCenterX = labelLayout.x + labelLayout.width / 2;
    const x = Clamp(labelCenterX - width / 2, 8, size - width - 8);
    const preferredY = labelLayout.y - height - 8;
    const y =
        preferredY > 44
            ? preferredY
            : labelLayout.y + labelLayout.height + 8;

    return {
        x,
        y,
        width,
        height,
        textX: x + width / 2,
        lineCenterYs: lines.map(
            (_line, index) => y + 6 + lineHeight / 2 + index * lineHeight
        ),
        lines,
    };
}

function GetLabelConnectorPoint(
    fromPoint: TPsycheRadarPoint,
    labelLayout: ReturnType<typeof GetLabelLayout>
) {
    const boxCenterX = labelLayout.x + labelLayout.width / 2;
    const boxCenterY = labelLayout.y + labelLayout.height / 2;
    const dx = boxCenterX - fromPoint.x;
    const dy = boxCenterY - fromPoint.y;
    const halfWidth = labelLayout.width / 2;
    const halfHeight = labelLayout.height / 2;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    const scale =
        absDx === 0 && absDy === 0
            ? 0
            : Math.min(
                  absDx === 0 ? Number.POSITIVE_INFINITY : halfWidth / absDx,
                  absDy === 0 ? Number.POSITIVE_INFINITY : halfHeight / absDy
              );

    return {
        x: boxCenterX - dx * scale,
        y: boxCenterY - dy * scale,
    };
}

function GetTaperedDashSegments(
    startPoint: TPsycheRadarPoint,
    controlPoint: TPsycheRadarPoint,
    endPoint: TPsycheRadarPoint
) {
    const curveLength = GetQuadraticBezierLength(
        startPoint,
        controlPoint,
        endPoint
    );
    const dashCount = Math.min(Math.max(Math.round(curveLength / 12), 10), 40);
    const segments: Array<{
        start: TPsycheRadarPoint;
        end: TPsycheRadarPoint;
        width: number;
    }> = [];

    for (let index = 0; index < dashCount; index += 1) {
        const tStart = index / dashCount;
        const tEnd = Math.min(1, tStart + 0.016);
        const start = GetQuadraticBezierPoint(
            startPoint,
            controlPoint,
            endPoint,
            tStart
        );
        const end = GetQuadraticBezierPoint(
            startPoint,
            controlPoint,
            endPoint,
            tEnd
        );

        segments.push({
            start,
            end,
            width: Lerp(5.5, 2.25, tStart),
        });
    }

    return segments;
}

function GetQuadraticBezierLength(
    startPoint: TPsycheRadarPoint,
    controlPoint: TPsycheRadarPoint,
    endPoint: TPsycheRadarPoint
) {
    const samples = 24;
    let length = 0;
    let previousPoint = startPoint;

    for (let index = 1; index <= samples; index += 1) {
        const t = index / samples;
        const nextPoint = GetQuadraticBezierPoint(
            startPoint,
            controlPoint,
            endPoint,
            t
        );
        const dx = nextPoint.x - previousPoint.x;
        const dy = nextPoint.y - previousPoint.y;
        length += Math.sqrt(dx * dx + dy * dy);
        previousPoint = nextPoint;
    }

    return length;
}

function GetQuadraticBezierPoint(
    startPoint: TPsycheRadarPoint,
    controlPoint: TPsycheRadarPoint,
    endPoint: TPsycheRadarPoint,
    t: number
) {
    const oneMinusT = 1 - t;

    return {
        x:
            oneMinusT * oneMinusT * startPoint.x +
            2 * oneMinusT * t * controlPoint.x +
            t * t * endPoint.x,
        y:
            oneMinusT * oneMinusT * startPoint.y +
            2 * oneMinusT * t * controlPoint.y +
            t * t * endPoint.y,
    };
}

function Lerp(start: number, end: number, t: number) {
    return start + (end - start) * t;
}

function Clamp(value: number, min: number, max: number) {
    return Math.max(min, Math.min(max, value));
}

function GetDistanceBetweenPoints(
    x1: number,
    y1: number,
    x2: number,
    y2: number
) {
    const dx = x2 - x1;
    const dy = y2 - y1;

    return Math.sqrt(dx * dx + dy * dy);
}

function GetDefaultLabelCenter(center: number, angleDeg: number) {
    return PolarToCartesian(center, center, 190, angleDeg);
}

function WrapLabelText(label: string, limit: number) {
    const trimmed = label.trim();

    if (trimmed.length <= limit) {
        return [trimmed];
    }

    const words = trimmed.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let currentLine = "";

    const pushCurrentLine = () => {
        if (currentLine) {
            lines.push(currentLine);
            currentLine = "";
        }
    };

    for (const word of words.length ? words : [trimmed]) {
        if (word.length > limit) {
            pushCurrentLine();
            lines.push(word);

            continue;
        }

        const nextLine = currentLine ? `${currentLine} ${word}` : word;

        if (nextLine.length > limit) {
            pushCurrentLine();
            currentLine = word;
        } else {
            currentLine = nextLine;
        }
    }

    pushCurrentLine();

    return lines.length ? lines : [trimmed.slice(0, limit)];
}

function NormalizeAngle(angleDeg: number) {
    const normalized = angleDeg % 360;
    return normalized < 0 ? normalized + 360 : normalized;
}

function GetImpactFlareStyle(
    spoke: TPsycheRadarSpoke,
    index: number,
    isActive: boolean,
    spokeVisibility: number
) {
    const seed = NormalizeAngle(spoke.angleDeg + index * 37) / 360;
    const flareAngleRadians = ((spoke.angleDeg - 90) * Math.PI) / 180;
    const outerRadius = Math.round(Lerp(13, 21, seed) + (isActive ? 3 : 0));
    const innerRadius = Math.round(outerRadius * Lerp(0.38, 0.5, seed));
    const outerOpacity = Clamp(
        (isActive ? 0.27 : 0.13) + spokeVisibility * Lerp(0.08, 0.2, seed),
        0.12,
        0.56
    );
    const innerOpacity = Clamp(
        (isActive ? 0.47 : 0.24) +
            spokeVisibility * Lerp(0.05, 0.12, 1 - seed),
        0.12,
        0.72
    );
    const outerColor = MixRgbColors(
        "#f3722c",
        "#f8961e",
        Lerp(0.18, 0.72, seed)
    );
    const innerColor = MixRgbColors(
        "#ffe08a",
        "#ffd166",
        Lerp(0.12, 0.68, 1 - seed)
    );
    const offsetMagnitude = Lerp(0.5, 2.4, seed) * (isActive ? 1.2 : 0.8);

    return {
        outerRadius,
        innerRadius,
        outerOpacity,
        innerOpacity,
        outerColor,
        innerColor,
        offsetX: Math.cos(flareAngleRadians) * offsetMagnitude,
        offsetY: Math.sin(flareAngleRadians) * offsetMagnitude,
    };
}

function MixRgbColors(fromHex: string, toHex: string, t: number) {
    const from = ParseHexColor(fromHex);
    const to = ParseHexColor(toHex);

    return `rgb(${Math.round(Lerp(from.r, to.r, t))}, ${Math.round(
        Lerp(from.g, to.g, t)
    )}, ${Math.round(Lerp(from.b, to.b, t))})`;
}

function ParseHexColor(hex: string) {
    const normalized = hex.replace("#", "").trim();
    const expanded =
        normalized.length === 3
            ? normalized
                  .split("")
                  .map((char) => `${char}${char}`)
                  .join("")
            : normalized;
    const value = Number.parseInt(expanded, 16);

    return {
        r: (value >> 16) & 255,
        g: (value >> 8) & 255,
        b: value & 255,
    };
}

function GetNextAvailableAngle(spokes: TPsycheRadarSpoke[]) {
    if (spokes.length === 0) {
        return 0;
    }

    const sortedAngles = spokes
        .map((spoke) => NormalizeAngle(spoke.angleDeg))
        .sort((a, b) => a - b);

    let bestAngle = sortedAngles[0];
    let bestGap = -1;

    for (let index = 0; index < sortedAngles.length; index += 1) {
        const currentAngle = sortedAngles[index];
        const nextAngle =
            index === sortedAngles.length - 1
                ? sortedAngles[0] + 360
                : sortedAngles[index + 1];
        const gap = nextAngle - currentAngle;

        if (gap > bestGap) {
            bestGap = gap;
            bestAngle = currentAngle + gap / 2;
        }
    }

    return NormalizeAngle(bestAngle);
}

function GetNextSpokeColor(index: number) {
    const colors = [
        "#53f29a",
        "#56c6ff",
        "#ff8c69",
        "#ffd166",
        "#bb86fc",
        "#ff6f91",
    ];

    return colors[index % colors.length];
}

export function GetRadarActionButtonStyleClass(
    kind: "add" | "rename" | "delete" | "reset",
    isPressed: boolean
) {
    const palette = {
        add: {
            background: "rgba(83, 242, 154, 0.14)",
            border: "rgba(83, 242, 154, 0.5)",
            color: "#d8ffec",
            pressedBackground: "rgba(18, 94, 59, 0.92)",
            pressedBorder: "rgba(83, 242, 154, 0.95)",
            pressedColor: "#ecfff5",
            hoverBackground: "rgba(83, 242, 154, 0.2)",
            hoverBorder: "rgba(83, 242, 154, 0.8)",
            activeBackground: "rgba(83, 242, 154, 0.1)",
            activeBorder: "rgba(83, 242, 154, 0.65)",
            shadow: "rgba(83, 242, 154, 0.1)",
        },
        rename: {
            background: "rgba(83, 242, 154, 0.1)",
            border: "rgba(83, 242, 154, 0.34)",
            color: "#d8ffec",
            pressedBackground: "rgba(23, 82, 57, 0.92)",
            pressedBorder: "rgba(83, 242, 154, 0.82)",
            pressedColor: "#ecfff5",
            hoverBackground: "rgba(83, 242, 154, 0.16)",
            hoverBorder: "rgba(83, 242, 154, 0.62)",
            activeBackground: "rgba(83, 242, 154, 0.08)",
            activeBorder: "rgba(83, 242, 154, 0.5)",
            shadow: "rgba(83, 242, 154, 0.08)",
        },
        delete: {
            background: "rgba(255, 99, 132, 0.1)",
            border: "rgba(255, 99, 132, 0.42)",
            color: "#ffd2dd",
            pressedBackground: "rgba(105, 25, 42, 0.94)",
            pressedBorder: "rgba(255, 99, 132, 0.9)",
            pressedColor: "#fff1f5",
            hoverBackground: "rgba(255, 99, 132, 0.16)",
            hoverBorder: "rgba(255, 99, 132, 0.72)",
            activeBackground: "rgba(255, 99, 132, 0.08)",
            activeBorder: "rgba(255, 99, 132, 0.56)",
            shadow: "rgba(255, 99, 132, 0.08)",
        },
        reset: {
            background: "rgba(255, 72, 72, 0.1)",
            border: "rgba(255, 72, 72, 0.42)",
            color: "#ffd2dd",
            pressedBackground: "rgba(98, 17, 24, 0.94)",
            pressedBorder: "rgba(255, 72, 72, 0.92)",
            pressedColor: "#fff1f1",
            hoverBackground: "rgba(255, 72, 72, 0.18)",
            hoverBorder: "rgba(255, 72, 72, 0.75)",
            activeBackground: "rgba(255, 72, 72, 0.08)",
            activeBorder: "rgba(255, 72, 72, 0.6)",
            shadow: "rgba(255, 72, 72, 0.08)",
        },
    }[kind];
    const pressedStyle = {
        background: palette.pressedBackground,
        borderColor: palette.pressedBorder,
        color: palette.pressedColor,
        boxShadow: `0 2px 10px rgba(0, 0, 0, 0.42) inset, 0 0 0 1px ${palette.shadow} inset`,
        filter: "brightness(0.9) saturate(1.14)",
        transform: "translateY(2px) scale(0.985)",
    };

    return StyleClassAuto({
        padding: "8px 12px",
        borderRadius: "8px",
        border: `1px solid ${palette.border}`,
        background: palette.background,
        color: palette.color,
        boxShadow: `0 0 0 1px ${palette.shadow} inset`,
        cursor: "pointer",
        transition:
            "transform 120ms ease, background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease, filter 120ms ease",
        willChange: "transform",
        ":hover": isPressed
            ? pressedStyle
            : {
                  background: palette.hoverBackground,
                  borderColor: palette.hoverBorder,
                  boxShadow: `0 0 0 1px ${palette.shadow} inset, 0 6px 18px rgba(0, 0, 0, 0.18)`,
                  filter: "brightness(1.06) saturate(1.08)",
                  transform: "translateY(-1px)",
              },
        ":active": isPressed
            ? pressedStyle
            : {
                  background: palette.activeBackground,
                  borderColor: palette.activeBorder,
                  boxShadow: `0 0 0 1px ${palette.shadow} inset, 0 2px 8px rgba(0, 0, 0, 0.16)`,
                  filter: "brightness(0.98) saturate(1.02)",
                  transform: "translateY(1px)",
              },
        ...(isPressed
            ? pressedStyle
            : {}),
        ":disabled": {
            cursor: "not-allowed",
            opacity: 0.45,
            filter: "grayscale(0.15)",
            transform: "none",
            boxShadow: `0 0 0 1px ${palette.shadow} inset`,
        },
    });
}

function CreateSpokeId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }

    return `spoke-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function LoadPersistedLibrary(): TPsycheRadarStoredLibrary {
    if (typeof localStorage === "undefined") {
        return GetDefaultLibrary();
    }

    try {
        const rawValue = localStorage.getItem(psycheRadarStorageKey);

        if (!rawValue) {
            return GetDefaultLibrary();
        }

        const parsed = ParseStoredLibrary(JSON.parse(rawValue));

        return parsed.library;
    } catch {
        return GetDefaultLibrary();
    }
}

function HasPersistedLibrary() {
    if (typeof localStorage === "undefined") {
        return false;
    }

    try {
        return localStorage.getItem(psycheRadarStorageKey) !== null;
    } catch {
        return false;
    }
}

function LoadActiveMapId(): string {
    if (typeof localStorage === "undefined") {
        return "";
    }

    try {
        return localStorage.getItem(psycheRadarActiveMapIdStorageKey) ?? "";
    } catch {
        return "";
    }
}

function SaveActiveMapId(selectedMapId: string) {
    if (typeof localStorage === "undefined") {
        return;
    }

    try {
        localStorage.setItem(psycheRadarActiveMapIdStorageKey, selectedMapId);
    } catch {
        // Best-effort UI preference persistence.
    }
}

function PersistLibraryToLocalStorage(library: TPsycheRadarStoredLibrary) {
    if (typeof localStorage === "undefined") {
        return;
    }

    localStorage.setItem(psycheRadarStorageKey, JSON.stringify(library));
}

function LoadLibraryFromServerAsync(): Promise<TPsycheRadarStoredLibrary> {
    const resource = `${resourceNameKeyValues}?key=eq.${encodeURIComponent(
        psycheRadarSyncedKey
    )}`;

    return GetSyncedRadarKeyValuesAsync(resource).then((keyValues) => {
        const serializedSnapshot = keyValues[0]?.value;

        if (!serializedSnapshot) {
            return GetDefaultLibrary();
        }

        try {
            const parsed = ParseStoredLibrary(JSON.parse(serializedSnapshot));

            return parsed.library;
        } catch {
            return GetDefaultLibrary();
        }
    });
}

async function SaveLibraryToServerAsync(
    library: TPsycheRadarStoredLibrary
): Promise<TPsycheRadarStoredLibrary> {
    const value = GetLibraryStorageValue(library);
    const resource = `${resourceNameKeyValues}?key=eq.${encodeURIComponent(
        psycheRadarSyncedKey
    )}`;
    const existingKeyValues = await GetSyncedRadarKeyValuesAsync(resource);

    try {
        if (existingKeyValues.length) {
            await ClientDataProvider.UpdateRecordAsync(resource, { value });
        } else {
            const newKeyValue = {
                ...GetDefaultKeyValue(),
                key: psycheRadarSyncedKey,
                value,
                expiry: 0,
            };

            delete (newKeyValue as Partial<IKeyValue>).id;

            await ClientDataProvider.AddRecordAsync(
                resourceNameKeyValues,
                newKeyValue
            );
        }
    } catch {
        return library;
    }

    return library;
}

function GetLibraryStorageValue(library: TPsycheRadarStoredLibrary): string {
    return JSON.stringify(library);
}

function ParseStoredLibrary(
    value: unknown
): {
    library: TPsycheRadarStoredLibrary;
    activeMapId: string;
} {
    if (Array.isArray(value)) {
        const maps = value
            .map((item, index) =>
                ParseStoredMap(item, `Map ${index + 1}`)
            )
            .filter((item): item is TPsycheRadarStoredMap => item !== undefined);

        const library = NormalizeLibrary({
            maps: maps.length > 0 ? maps : [CreateBlankMap("Map 1")],
            updatedAt: new Date().toISOString(),
        });

        return {
            library,
            activeMapId: library.maps[0]?.id ?? "",
        };
    }

    if (!value || typeof value !== "object") {
        const library = GetDefaultLibrary();
        return {
            library,
            activeMapId: library.maps[0]?.id ?? "",
        };
    }

    const candidate = value as Record<string, unknown>;

    if (Array.isArray(candidate.maps)) {
        const maps = candidate.maps
            .map((item, index) =>
                ParseStoredMap(item, `Map ${index + 1}`)
            )
            .filter((item): item is TPsycheRadarStoredMap => item !== undefined);
        const library = NormalizeLibrary({
            maps: maps.length > 0 ? maps : [CreateBlankMap("Map 1")],
            updatedAt:
                typeof candidate.updatedAt === "string" &&
                IsValidIsoDate(candidate.updatedAt)
                    ? candidate.updatedAt
                    : new Date().toISOString(),
        });
        const activeMapId =
            typeof candidate.activeMapId === "string" &&
            library.maps.some((map) => map.id === candidate.activeMapId)
                ? candidate.activeMapId
                : library.maps[0]?.id ?? "";

        return {
            library,
            activeMapId,
        };
    }

    const snapshot = ParseStoredSnapshot(value) ?? GetDefaultSnapshot();
    const map = CreateMapFromSnapshot(snapshot, "Map 1");
    const library = NormalizeLibrary({
        maps: [map],
        updatedAt: snapshot.updatedAt,
    });

    return {
        library,
        activeMapId: map.id,
    };
}

function ParseStoredMap(
    value: unknown,
    fallbackName: string
): TPsycheRadarStoredMap | undefined {
    if (!value || typeof value !== "object") {
        return undefined;
    }

    const candidate = value as Record<string, unknown>;
    const id =
        typeof candidate.id === "string" && candidate.id.trim()
            ? candidate.id
            : GetNextMapId();
    const name =
        typeof candidate.name === "string" && candidate.name.trim()
            ? candidate.name
            : fallbackName;

    if (candidate.snapshot && typeof candidate.snapshot === "object") {
        const snapshot = ParseStoredSnapshot(candidate.snapshot) ?? GetDefaultSnapshot();

        return {
            id,
            name,
            snapshot,
        };
    }

    const snapshot = ParseStoredSnapshot(value) ?? GetDefaultSnapshot();

    return {
        id,
        name,
        snapshot,
    };
}

function CreateMapFromSnapshot(
    snapshot: TPsycheRadarStoredSnapshot,
    name: string,
    id = GetNextMapId()
): TPsycheRadarStoredMap {
    return {
        id,
        name,
        snapshot,
    };
}

function CreateBlankMap(name: string, id = GetNextMapId()): TPsycheRadarStoredMap {
    return CreateMapFromSnapshot(GetDefaultSnapshot(), name, id);
}

function GetNextMapId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }

    return `map-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function GetNextMapName(maps: TPsycheRadarStoredMap[]) {
    const usedNames = new Set(maps.map((map) => map.name));

    for (let index = 1; index < 1000; index += 1) {
        const candidate = `Map ${index}`;

        if (!usedNames.has(candidate)) {
            return candidate;
        }
    }

    return `Map ${maps.length + 1}`;
}

function GetSelectedMap(
    library: TPsycheRadarStoredLibrary,
    selectedMapId: string
): TPsycheRadarStoredMap {
    if (library.maps.length === 0) {
        return CreateBlankMap("Map 1");
    }

    return (
        library.maps.find((map) => map.id === selectedMapId) ?? library.maps[0]
    );
}

function UpdateLibraryMap(
    library: TPsycheRadarStoredLibrary,
    selectedMapId: string,
    update: (currentMap: TPsycheRadarStoredMap) => TPsycheRadarStoredMap
): TPsycheRadarStoredLibrary {
    if (library.maps.length === 0) {
        return NormalizeLibrary({
            maps: [update(CreateBlankMap("Map 1"))],
            updatedAt: new Date().toISOString(),
        });
    }

    const targetMapId =
        library.maps.find((map) => map.id === selectedMapId)?.id ??
        library.maps[0].id;

    const nextMaps = library.maps.map((map) =>
        map.id === targetMapId ? update(map) : map
    );

    return NormalizeLibrary({
        maps: nextMaps,
        updatedAt: new Date().toISOString(),
    });
}

function NormalizeLibrary(
    library: TPsycheRadarStoredLibrary
): TPsycheRadarStoredLibrary {
    return {
        maps: library.maps.length > 0 ? library.maps : [CreateBlankMap("Map 1")],
        updatedAt: IsValidIsoDate(library.updatedAt)
            ? library.updatedAt
            : new Date().toISOString(),
    };
}

function GetDefaultLibrary(): TPsycheRadarStoredLibrary {
    return NormalizeLibrary({
        maps: [CreateBlankMap("Map 1")],
        updatedAt: new Date().toISOString(),
    });
}

function CompareLibraries(
    left: TPsycheRadarStoredLibrary,
    right: TPsycheRadarStoredLibrary
) {
    const leftTime = GetLibraryEffectiveTime(left);
    const rightTime = GetLibraryEffectiveTime(right);

    if (leftTime === null && rightTime === null) {
        return 0;
    }

    if (leftTime === null) {
        return -1;
    }

    if (rightTime === null) {
        return 1;
    }

    if (leftTime === rightTime) {
        return 0;
    }

    return leftTime > rightTime ? 1 : -1;
}

function GetLibraryEffectiveTime(library: TPsycheRadarStoredLibrary) {
    if (IsValidIsoDate(library.updatedAt)) {
        return new Date(library.updatedAt).getTime();
    }

    return null;
}

function GetPsycheRadarExportFilename(mapName: string) {
    const sanitizedMapName = mapName
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    if (!sanitizedMapName) {
        return psycheRadarExportFilename;
    }

    return `${sanitizedMapName.toLowerCase()}.json`;
}

function GetPsycheRadarMapNameFromFilename(filename: string) {
    const baseName = filename.replace(/\.[^/.]+$/, "");
    const sanitizedBaseName = baseName.trim();

    if (!sanitizedBaseName) {
        return "Imported map";
    }

    return sanitizedBaseName;
}

function BuildImportedMapName(baseName: string, index: number) {
    return index === 0 ? baseName : `${baseName} ${index + 1}`;
}

function GetNormalizedMapNameKey(mapName: string) {
    return mapName.trim().toLowerCase();
}

function ShouldUseImportedMapFilenameName(mapName: string) {
    const trimmedMapName = mapName.trim();

    if (!trimmedMapName) {
        return true;
    }

    return /^Map \d+$/i.test(trimmedMapName);
}

function ParseStoredSnapshot(value: unknown) {
    if (Array.isArray(value)) {
        const parsedSpokes = value
            .map((item) => ParsePersistedSpoke(item))
            .filter((item): item is TPsycheRadarSpoke => item !== undefined);

        if (parsedSpokes.length === 0) {
            return undefined;
        }

        return {
            spokes: parsedSpokes,
            updatedAt: new Date().toISOString(),
            hidePaths: false,
            hideCenter: false,
        };
    }

    if (!value || typeof value !== "object") {
        return undefined;
    }

    const candidate = value as Record<string, unknown>;
    const updatedAt =
        typeof candidate.updatedAt === "string" && IsValidIsoDate(candidate.updatedAt)
            ? candidate.updatedAt
            : new Date().toISOString();
    const resetAt =
        typeof candidate.resetAt === "string" && IsValidIsoDate(candidate.resetAt)
            ? candidate.resetAt
            : undefined;
    const spokes = Array.isArray(candidate.spokes)
        ? candidate.spokes
              .map((item) => ParsePersistedSpoke(item))
              .filter((item): item is TPsycheRadarSpoke => item !== undefined)
        : [];

    if (spokes.length === 0 && !resetAt) {
        return undefined;
    }

    return {
        spokes,
        updatedAt,
        hidePaths:
            typeof candidate.hidePaths === "boolean"
                ? candidate.hidePaths
                : false,
        hideCenter:
            typeof candidate.hideCenter === "boolean"
                ? candidate.hideCenter
                : false,
        ...(resetAt ? { resetAt } : {}),
    };
}

function ParsePersistedSpoke(value: unknown) {
    if (!value || typeof value !== "object") {
        return undefined;
    }

    const candidate = value as Record<string, unknown>;
    const id = typeof candidate.id === "string" ? candidate.id : CreateSpokeId();
    const label = typeof candidate.label === "string" ? candidate.label : "xyz";
    const angleDeg =
        typeof candidate.angleDeg === "number" && Number.isFinite(candidate.angleDeg)
            ? candidate.angleDeg
            : 0;
    const labelCenterX =
        typeof candidate.labelCenterX === "number" &&
        Number.isFinite(candidate.labelCenterX)
            ? candidate.labelCenterX
            : undefined;
    const labelCenterY =
        typeof candidate.labelCenterY === "number" &&
        Number.isFinite(candidate.labelCenterY)
            ? candidate.labelCenterY
            : undefined;
    const color =
        typeof candidate.color === "string" ? candidate.color : GetNextSpokeColor(0);
    const curve =
        typeof candidate.curve === "number" && Number.isFinite(candidate.curve)
            ? candidate.curve
            : 0.08;
    const notes =
        typeof candidate.notes === "string" ? candidate.notes : "";

    return {
        id,
        label,
        angleDeg,
        labelCenterX,
        labelCenterY,
        color,
        curve,
        notes,
    };
}

function GetDefaultSnapshot(): TPsycheRadarStoredSnapshot {
    return {
        spokes: emptyRadarSpokes,
        updatedAt: new Date().toISOString(),
        hidePaths: false,
        hideCenter: false,
    };
}

function IsValidIsoDate(value: string) {
    return !Number.isNaN(new Date(value).getTime());
}

async function GetSyncedRadarKeyValuesAsync(
    resource: string
): Promise<IKeyValue[]> {
    try {
        return (
            (await ClientDataProvider.GetRecordsAsync<IKeyValue>(resource)) || []
        );
    } catch {
        return [];
    }
}
